import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, Gem } from 'lucide-react';
import api from '../utils/api';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const { settings } = useSettings();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
      // In development, show the reset link directly
      if (response.data.reset_link) {
        setResetLink(response.data.reset_link);
      }
      toast.success('Password reset instructions sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset instructions');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-6" data-testid="forgot-password-success">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="font-serif text-2xl font-bold text-[#1a3a5c] mb-4">Check Your Email</h1>
            <p className="text-gray-600 mb-6">
              If an account exists for <strong>{email}</strong>, you will receive password reset instructions.
            </p>
            
            {/* Development mode: Show reset link directly */}
            {resetLink && (
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-6 text-left">
                <p className="text-sm text-amber-800 font-medium mb-2">Development Mode:</p>
                <p className="text-xs text-amber-700 break-all">
                  <Link to={resetLink.replace(window.location.origin, '')} className="text-[#1a3a5c] underline hover:text-[#C9A967]">
                    Click here to reset your password
                  </Link>
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full bg-[#1a3a5c] text-white py-3 rounded-sm font-serif tracking-wide hover:bg-[#1f4268] transition-all duration-300 text-center"
              >
                Back to Login
              </Link>
              <button
                onClick={() => setSubmitted(false)}
                className="text-[#1a3a5c] hover:text-[#C9A967] transition-colors text-sm"
              >
                Try a different email
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6" data-testid="forgot-password-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-3 mb-6">
            {settings.logo_url ? (
              <img 
                src={`${settings.logo_url}${settings.logo_url?.includes('?') ? '&' : '?'}v=${settings.updated_at || '1'}`}
                alt={settings.store_name || 'Marissa Gold And Diamonds'}
                className="h-16 w-16 object-contain"
              />
            ) : (
              <div className="h-16 w-16 bg-gradient-to-br from-[#C9A967] to-[#B8860B] rounded-lg flex items-center justify-center">
                <Gem className="h-10 w-10 text-white" />
              </div>
            )}
            <span className="text-3xl font-serif font-bold text-[#1a3a5c]">{settings.store_name || 'Marissa Gold And Diamonds'}</span>
          </Link>
          <h1 className="font-serif text-3xl font-bold text-[#1a3a5c] mb-2" data-testid="forgot-password-title">
            Forgot Password?
          </h1>
          <p className="text-gray-600">
            Enter your email and we will send you reset instructions
          </p>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:border-[#1a3a5c] transition-colors"
                  placeholder="Enter your email"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1a3a5c] text-white py-3 rounded-sm font-serif tracking-wide hover:bg-[#1f4268] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="forgot-submit-button"
            >
              {loading ? 'Sending...' : 'Send Reset Instructions'}
            </button>
          </form>

          <div className="mt-6">
            <Link
              to="/login"
              className="flex items-center justify-center space-x-2 text-[#1a3a5c] hover:text-[#C9A967] transition-colors text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Login</span>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
